import React, { forwardRef, useEffect, useState } from 'react';
import OazaLogo from "../../image/logo/logo_color_300x70.png";

const createInvoiceSignature = (value = '') => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return `sig_${hash.toString(16)}_${value.length}`;
};

const getImageAsBase64 = async (url) => {
  const response = await fetch(url, { mode: "cors" });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const processInvoiceHtml = async (rawHtml) => {
  let html = rawHtml;

  try {
    // Find all img tags with src attributes
    const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
    const matches = [...html.matchAll(imgRegex)];


    for (const match of matches) {
      const fullImgTag = match[0];
      const imageUrl = match[1];

      if (imageUrl.startsWith('http') || imageUrl.startsWith('//')) {
        let base64Image = null;

        try {
          // Try CORS proxy services (working solution)
          const proxyServices = [
            `https://images.weserv.nl/?url=${encodeURIComponent(imageUrl)}&w=300&h=100&fit=contain`,
            `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(imageUrl)}`,
          ];

          for (const proxyUrl of proxyServices) {
            try {
              base64Image = await getImageAsBase64(proxyUrl);
              if (base64Image) {
                const newImgTag = fullImgTag.replace(
                  /src=["']([^"']+)["']/i,
                  `src="${base64Image}"`
                );
                html = html.replace(fullImgTag, newImgTag);
                break;
              }
            } catch (proxyError) {
              continue; // Try next proxy
            }
          }

          // If all proxies failed, use local logo fallback
          if (!base64Image) {
            throw new Error('All proxy services failed');
          }
        } catch (error) {
          // Fallback to local logo instead of placeholder
          try {
            // Convert local logo to base64 for PDF compatibility
            const localLogoBase64 = await getImageAsBase64(OazaLogo);

            const newImgTag = fullImgTag.replace(
              /src=["']([^"']+)["']/i,
              `src="${localLogoBase64}"`
            );
            html = html.replace(fullImgTag, newImgTag);
          } catch (localError) {
            console.log('Local logo conversion failed:', localError);
            // Final fallback - use direct path (might work in some cases)
            const newImgTag = fullImgTag.replace(
              /src=["']([^"']+)["']/i,
              `src="${OazaLogo}"`
            );
            html = html.replace(fullImgTag, newImgTag);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error processing images:', error);
  }

  // The backend sends a full HTML body. Strip body/html wrappers because this is rendered inside a div.
  html = html
    .replace(/<\/?html[^>]*>/gi, '')
    .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
    .replace(/<\/?body[^>]*>/gi, '')
    .trim();

  // Frontend fallback for missing driver name from API payload.
  html = html.replace(
    /(<strong>\s*EV Driver Name:\s*<\/strong>)\s*<\/p>/gi,
    '$1 NA</p>'
  );

  return html;
};

const InvoiceTemplate = forwardRef(({ transactionData }, ref) => {
  const [processedHtml, setProcessedHtml] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [invoiceSignature, setInvoiceSignature] = useState('');

  useEffect(() => {
    let cancelled = false;

    const prepareInvoice = async () => {
      if (!(typeof transactionData === 'string' && transactionData.includes('<'))) {
        setProcessedHtml('');
        setInvoiceSignature('');
        setIsProcessing(false);
        return;
      }

      const nextSignature = createInvoiceSignature(transactionData);
      setInvoiceSignature(nextSignature);
      setIsProcessing(true);

      try {
        const html = await processInvoiceHtml(transactionData);
        if (!cancelled) {
          setProcessedHtml(html);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error preparing invoice HTML:', error);
          setProcessedHtml('');
        }
      } finally {
        if (!cancelled) {
          setIsProcessing(false);
        }
      }
    };

    prepareInvoice();

    return () => {
      cancelled = true;
    };
  }, [transactionData]);
  
  // Show loading state while processing images
  if (isProcessing) {
    return (
      <div
        ref={ref}
        data-invoice-root="true"
        data-invoice-ready="false"
        data-invoice-signature={invoiceSignature}
        style={{ padding: '20px', textAlign: 'center', backgroundColor: '#ffffff', color: '#333333' }}
      >
        <p>Processing invoice template and loading images...</p>
      </div>
    );
  }
  
  // If transactionData is HTML string, render the processed version
  if (processedHtml) {
    return (
      <div 
        ref={ref}
        data-invoice-root="true"
        data-invoice-ready="true"
        data-invoice-signature={invoiceSignature}
        dangerouslySetInnerHTML={{ __html: processedHtml }}
        style={{ 
          fontFamily: 'Arial, sans-serif',
          maxWidth: '800px',
          margin: '0 auto',
          backgroundColor: '#ffffff',
          color: '#333333',
          padding: '0',  // Remove padding
          boxSizing: 'border-box'
        }}
      />
    );
  }
  
  // If no HTML content, render a simple message
  return (
    <div
      ref={ref}
      data-invoice-root="true"
      data-invoice-ready="false"
      data-invoice-signature={invoiceSignature}
      style={{ padding: '20px', textAlign: 'center', backgroundColor: '#ffffff', color: '#333333' }}
    >
      <p>No invoice data available</p>
    </div>
  );
});

InvoiceTemplate.displayName = 'InvoiceTemplate';

export default InvoiceTemplate;
